import { Component, type ReactNode } from 'react'
import i18n from '../lib/i18n'

interface Props {
  children: ReactNode
  fallback?: ReactNode
}

interface State {
  hasError: boolean
  error: Error | null
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    if (import.meta.env.DEV) {
      console.error('ErrorBoundary caught:', error, errorInfo)
    }

    // Vite chunk-load failure recovery (belt-and-braces fallback).
    //
    // When Vite re-optimizes deps in dev or ships a new deploy with re-hashed
    // chunks in prod, lazy-loaded pages can fail with "Failed to fetch
    // dynamically imported module". main.tsx has a global `vite:preloadError`
    // + `unhandledrejection` listener that should catch this first, but if
    // React's Suspense swallows the rejection before the window handlers
    // fire, the error lands here. Hard-reload to recover.
    //
    // Guarded by a sessionStorage one-shot to prevent reload loops if the
    // reload itself re-triggers the error.
    const msg = error?.message ?? ''
    const isChunkLoadError =
      msg.includes('Failed to fetch dynamically imported module') ||
      msg.includes('error loading dynamically imported module') ||
      msg.includes('Importing a module script failed')

    if (isChunkLoadError) {
      try {
        if (sessionStorage.getItem('__bc_chunk_reload_fired__') !== '1') {
          sessionStorage.setItem('__bc_chunk_reload_fired__', '1')
          console.warn('[bc] ErrorBoundary: chunk load failure, hard-reloading')
          window.location.reload()
        }
      } catch {
        // sessionStorage unavailable — still try to reload once
        window.location.reload()
      }
    }
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback
      return (
        <div
          data-testid="app-error-boundary"
          className="flex min-h-screen items-center justify-center bg-[#0a0a12] p-6"
          style={{
            backgroundImage:
              'radial-gradient(circle at 20% 30%, rgba(168, 85, 247, 0.10) 0%, transparent 50%), radial-gradient(circle at 80% 70%, rgba(139, 92, 246, 0.08) 0%, transparent 50%)',
          }}
        >
          <div className="relative w-full max-w-md overflow-hidden rounded-3xl border border-white/[0.08] bg-white/[0.04] p-8 text-center shadow-[0_24px_80px_-20px_rgba(139,92,246,0.35)] backdrop-blur-2xl">
            {/* Brand wordmark instead of a warning emoji — keeps the
                failure screen on-brand. Identity first, apology second. */}
            <div className="mb-6 flex justify-center">
              <span className="text-sm font-medium tracking-[0.02em] text-[var(--bn-text-muted)]">
                better<span className="text-[var(--bn-accent)]">/</span>close
              </span>
            </div>
            {/* Hairline accent rule matches the CompletionScreen divider */}
            <div
              aria-hidden="true"
              className="mx-auto mb-6 h-[1px] w-10"
              style={{ background: 'linear-gradient(to right, transparent, rgba(168, 85, 247, 0.45), transparent)' }}
            />
            <h1 className="mb-2 text-xl font-light tracking-tight text-white">
              {i18n.t('error_boundary.title', { ns: 'components' })}
            </h1>
            <p className="mb-7 text-sm font-light leading-relaxed text-[var(--bn-text-muted)]">
              {i18n.t('error_boundary.description', { ns: 'components' })}
            </p>
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="inline-flex items-center gap-2 rounded-xl bg-purple-600 border border-purple-500/30 px-6 py-2.5 text-sm font-medium text-white shadow-[0_8px_24px_-8px_rgba(168,85,247,0.45)] hover:shadow-[0_12px_32px_-8px_rgba(168,85,247,0.55)] hover:-translate-y-px active:translate-y-0 transition-all duration-150"
            >
              {i18n.t('error_boundary.refresh', { ns: 'components' })}
            </button>
          </div>
        </div>
      )
    }
    return this.props.children
  }
}
